import { useEffect, useState } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useAuthContext } from '@/contexts/auth-context'

const HoomiLogo = '/assets/hoomi-logo.png'

export default function Home() {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, loading, logout } = useAuthContext()
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(5)
  const [ready, setReady] = useState(false)

  // Read and immediately strip sensitive params from URL/history
  const [redirectionUrl] = useState(() => searchParams.get('redirection_url'))
  const [jwtToken] = useState(() => searchParams.get('jwt_token'))
  const [platform] = useState(() => searchParams.get('platform'))

  const isMobile = platform === 'mobile' && !!jwtToken

  useEffect(() => {
    // Remove sensitive params from browser URL bar without a navigation
    window.history.replaceState({}, '', window.location.pathname)
  }, [])

  // For mobile flow: skip auth context check, go straight to countdown
  useEffect(() => {
    if (isMobile) {
      setReady(true)
      return
    }
    if (loading) return
    if (!isAuthenticated) {
      window.location.href = '/auth'
      return
    }
    setReady(true)
  }, [isMobile, isAuthenticated, loading])

  useEffect(() => {
    if (!ready) return
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
      return () => clearTimeout(timer)
    }
    handleAction()
  }, [ready, countdown])

  const handleAction = () => {
    if (isMobile) {
      logout()
      window.close()
    } else if (redirectionUrl) {
      window.location.href = redirectionUrl
    } else {
      navigate('/authorized')
    }
  }

  if (!isMobile && (loading || !ready)) {
    return (
      <div className="bg-black flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 text-white relative">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0"></div>
        <div className="absolute top-0 left-0 w-full p-4 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src={HoomiLogo} alt="Hoomi" width={24} height={24} />
            <span className="font-semibold text-purple-300">Hoomi Social</span>
          </Link>
        </div>
        <div className="w-full max-w-md z-10 flex flex-col items-center gap-6">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"></div>
          <p className="text-xl text-white">Checking authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 text-white relative">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0"></div>
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-500/10 rounded-full blur-3xl"></div>

      <div className="absolute top-0 left-0 w-full p-4 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src={HoomiLogo} alt="Hoomi" width={24} height={24} />
          <span className="font-semibold text-purple-300">Hoomi Social</span>
        </Link>
      </div>

      <div className="w-full max-w-md z-10 flex flex-col items-center gap-8">
        {/* Success icon */}
        <div className="h-20 w-20 rounded-full bg-purple-500/20 flex items-center justify-center border-4 border-purple-500">
          <svg className="w-10 h-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div className="text-center space-y-3">
          <h1 className="text-3xl font-bold text-white">Authentication Successful!</h1>
          <p className="text-lg text-white/80">
            {isMobile
              ? 'Window closing in'
              : redirectionUrl
                ? 'You will be redirected in'
                : 'Taking you to your account in'}
          </p>
        </div>

        {/* Countdown circle */}
        <div className="relative">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-purple-500/20" />
            <circle
              cx="64" cy="64" r="56"
              stroke="currentColor" strokeWidth="8" fill="none"
              strokeLinecap="round"
              className="text-purple-500 transition-all duration-1000"
              style={{
                strokeDasharray: `${2 * Math.PI * 56}`,
                strokeDashoffset: `${2 * Math.PI * 56 * (1 - countdown / 5)}`,
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-6xl font-bold text-white">{countdown}</span>
          </div>
        </div>

        {redirectionUrl && !isMobile && (
          <div className="text-center">
            <p className="text-sm text-white/70 mb-2">Redirecting to:</p>
            <p className="text-white font-medium break-all px-4">{redirectionUrl}</p>
          </div>
        )}

        {isMobile && (
          <p className="text-sm text-white/70 text-center">
            You can return to the app. This window will close automatically.
          </p>
        )}

        <button
          onClick={handleAction}
          className="text-purple-300 underline hover:text-purple-200 transition-colors text-sm"
        >
          {isMobile
            ? 'Close now'
            : redirectionUrl
              ? "Click here if you're not redirected automatically"
              : 'Go now'}
        </button>
      </div>
    </div>
  )
}
